/**
 * Helper functions for PostForm component
 */

// Convert datetime-local value (YYYY-MM-DDTHH:mm) to ISO string, treating input as CT time
// Use a simple binary search approach to find the correct UTC time
export const convertCTToISO = (datetimeLocalString) => {
  if (!datetimeLocalString) return null;
  
  console.log('[convertCTToISO] Input:', datetimeLocalString);
  
  // Parse the input: "YYYY-MM-DDTHH:mm" - we interpret this as CT time
  const [datePart, timePart] = datetimeLocalString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
  
  console.log('[convertCTToISO] Parsed CT time:', { year, month, day, hours, minutes });
  
  // Start with a reasonable guess: CT is typically UTC-6 (or UTC-5 in DST)
  // Create UTC date assuming CT is UTC-6
  let utcDate = new Date(Date.UTC(year, month - 1, day, hours + 6, minutes, 0));
  console.log('[convertCTToISO] Initial UTC guess:', utcDate.toISOString());
  
  // Refine by checking what it looks like in CT and adjusting
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Try a few adjustments to get exact match
  for (let attempt = 0; attempt < 5; attempt++) {
    const ctFormatted = formatter.format(utcDate);
    const match = ctFormatted.match(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/);
    
    if (!match) {
      console.log('[convertCTToISO] No match found for:', ctFormatted);
      break;
    }
    
    const [, fmtMonth, fmtDay, fmtYear, fmtHour, fmtMinute] = match.map(Number);
    console.log(`[convertCTToISO] Attempt ${attempt + 1}: UTC ${utcDate.toISOString()} = CT ${fmtYear}-${fmtMonth}-${fmtDay} ${fmtHour}:${fmtMinute}`);
    
    // Check if we have a match
    if (fmtYear === year && fmtMonth === month && fmtDay === day && 
        fmtHour === hours && fmtMinute === minutes) {
      console.log('[convertCTToISO] Match found! Returning:', utcDate.toISOString());
      return utcDate.toISOString();
    }
    
    // Calculate adjustment needed
    const targetMinutes = hours * 60 + minutes;
    const actualMinutes = fmtHour * 60 + fmtMinute;
    const minuteDiff = targetMinutes - actualMinutes;
    
    // Also account for date difference
    const targetDate = new Date(year, month - 1, day);
    const actualDate = new Date(fmtYear, fmtMonth - 1, fmtDay);
    const dayDiff = Math.round((targetDate - actualDate) / (1000 * 60 * 60 * 24));
    
    console.log(`[convertCTToISO] Adjusting: minuteDiff=${minuteDiff}, dayDiff=${dayDiff}`);
    
    // Adjust UTC date
    utcDate = new Date(utcDate.getTime() - (minuteDiff * 60 * 1000) - (dayDiff * 24 * 60 * 60 * 1000));
  }
  
  console.log('[convertCTToISO] Final result:', utcDate.toISOString());
  return utcDate.toISOString();
};

// eslint-disable-next-line no-unused-vars
export const convertISOToCTLocal = (isoString) => {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  // Convert to CT timezone
  const ctDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  
  const year = ctDate.getFullYear();
  const month = String(ctDate.getMonth() + 1).padStart(2, '0');
  const day = String(ctDate.getDate()).padStart(2, '0');
  const hours = String(ctDate.getHours()).padStart(2, '0');
  const minutes = String(ctDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to get default scheduled time: 2 hours from now in CT with :00 minutes
export const getDefaultScheduledTimeCT = () => {
  const now = new Date();
  // Get current time in CT timezone
  const ctDateStr = now.toLocaleString('en-US', { 
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Parse CT time: format is "MM/DD/YYYY, HH:mm"
  const parts = ctDateStr.match(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/);
  if (!parts) return null;
  
  const [, month, day, year, hour, minute] = parts.map(Number);
  
  // Create date in CT, add 2 hours, set minutes to 00
  const ctDate = new Date(year, month - 1, day, hour, minute, 0);
  ctDate.setHours(ctDate.getHours() + 2);
  ctDate.setMinutes(0);
  
  // Format for datetime-local input (YYYY-MM-DDTHH:mm)
  const formattedYear = ctDate.getFullYear();
  const formattedMonth = String(ctDate.getMonth() + 1).padStart(2, '0');
  const formattedDay = String(ctDate.getDate()).padStart(2, '0');
  const formattedHour = String(ctDate.getHours()).padStart(2, '0');
  
  return `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHour}:00`;
};

// Helper function to ensure links have default attributes
export const ensureLinkDefaults = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Process links to ensure they have target="_blank" and rel="noopener noreferrer"
  return htmlContent.replace(
    /<a\s+([^>]*?)href="([^"]*?)"([^>]*?)>/gi,
    (match, before, url, after) => {
      // Check if target attribute already exists
      const hasTarget = /target="/i.test(before + after);
      // Check if rel attribute already exists
      const hasRel = /rel="/i.test(before + after);
      
      let newAttrs = before + after;
      
      // Add target if not present
      if (!hasTarget) {
        newAttrs += ' target="_blank"';
      }
      
      // Add rel if not present
      if (!hasRel) {
        newAttrs += ' rel="noopener noreferrer"';
      }
      
      return `<a ${newAttrs} href="${url}">`;
    }
  );
};
